/**
 * Shared logic for the Special Enrollment Period (SEP) eligibility tool and the
 * Open Enrollment countdown. Kept in one place so the page copy, the results
 * math, and the countdown can never drift apart.
 */

export const SEP_WINDOW_DAYS = 60;

/** Open Enrollment opens 12:00am Eastern on November 1 (EDT = UTC-4). */
export const OEP_START_UTC = Date.UTC(2026, 10, 1, 4, 0, 0);

export type SepEventOption = {
  value: string;
  label: string;
  helper?: string;
};

export const sepEventOptions: SepEventOption[] = [
  {
    value: "Marriage or divorce",
    label: "A marriage or a divorce",
    helper: "Either one opens a window — no need to explain which.",
  },
  {
    value: "New baby or adoption",
    label: "A new baby, an adoption, or a foster placement",
  },
  {
    value: "Lost health coverage",
    label: "Coverage ended or is ending",
    helper: "A job change, aging off a parent's plan, COBRA running out, or Medicaid/FAMIS ending.",
  },
  {
    value: "Moved to a new address",
    label: "A move to a new address",
    helper: "A new ZIP code, county, or state.",
  },
  { value: "Turning 26", label: "Turning 26 — or getting close to it" },
  { value: "Change in immigration status", label: "A change in immigration or citizenship status" },
  { value: "None of these / not sure", label: "None of these, or I'm not sure" },
];

export const sepCoverageOptions = [
  "No health coverage right now",
  "An employer plan",
  "Medicaid or FAMIS",
  "COBRA",
  "A Marketplace plan already",
];

export type SepResultKind = "open" | "closed" | "unsure";

export type SepResult = {
  kind: SepResultKind;
  /** ISO date (yyyy-mm-dd) the 60-day window closes, when one applies. */
  windowCloseDate?: string;
  daysLeft?: number;
  daysSinceEvent?: number;
};

function parseDateOnly(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (!match) return null;
  const [, y, m, d] = match;
  const date = new Date(Date.UTC(Number(y), Number(m) - 1, Number(d)));
  return Number.isNaN(date.getTime()) ? null : date;
}

function toDateOnly(date: Date): string {
  return date.toISOString().slice(0, 10);
}

const DAY_MS = 86_400_000;

/**
 * Works out where the visitor stands. Future-dated events (a baby due next
 * month, a plan ending in three weeks) still count as an open window.
 */
export function evaluateSep(event: string, eventDate: string, today = new Date()): SepResult {
  if (!event || event === "None of these / not sure") return { kind: "unsure" };

  const parsed = parseDateOnly(eventDate);
  if (!parsed) return { kind: "unsure" };

  const todayUtc = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
  const daysSinceEvent = Math.round((todayUtc - parsed.getTime()) / DAY_MS);

  const close = new Date(parsed.getTime() + SEP_WINDOW_DAYS * DAY_MS);
  const windowCloseDate = toDateOnly(close);
  const daysLeft = Math.round((close.getTime() - todayUtc) / DAY_MS);

  if (daysSinceEvent <= SEP_WINDOW_DAYS) {
    return { kind: "open", windowCloseDate, daysLeft: Math.max(daysLeft, 0), daysSinceEvent };
  }
  return { kind: "closed", windowCloseDate, daysSinceEvent };
}

/** "November 12, 2026" */
export function formatLongDate(isoDate: string): string {
  const parsed = parseDateOnly(isoDate);
  if (!parsed) return isoDate;
  return parsed.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
}

/** Loose email check, matching the tolerance of the rest of the site's forms. */
export function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[a-z]{2,}$/i.test(value.trim());
}

/** Accepts any input that contains 10 or 11 digits. */
export function isValidPhone(value: string): boolean {
  const digits = value.replace(/\D/g, "");
  return digits.length === 10 || digits.length === 11;
}

/** (571) 571-1491 as you type. */
export function formatPhone(value: string): string {
  const digits = value.replace(/\D/g, "").slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}
